import React, { useCallback, useEffect, useRef, useState } from 'react';
import TimeInputSlider from './TimeInputSlider';
import SelectSpeed from './SelectSpeed';
import { useSplitContext } from 'providers/SplitProvider';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';

const TICK_MS = 100;
const SKIP_MS = 30 * 1000;

const formatDisplayTime = (time: number) => {
  const totalSeconds = Math.max(0, Math.floor(time / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

const Timer = () => {
  const { time, setTime, maxTime, options } = useSplitContext();

  const [isPlay, setIsPlay] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeRef = useRef(time);
  const speedRef = useRef(options.speed);
  const maxTimeRef = useRef(maxTime);
  const isPlayRef = useRef(isPlay);

  timeRef.current = time;
  speedRef.current = options.speed;
  maxTimeRef.current = maxTime;
  isPlayRef.current = isPlay;

  const clearTimerInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stopTimer = useCallback(() => {
    clearTimerInterval();
    setIsPlay(false);
  }, [clearTimerInterval]);

  const startTimer = useCallback(() => {
    clearTimerInterval();

    const startTime = timeRef.current >= maxTimeRef.current ? 0 : timeRef.current;
    if (startTime !== timeRef.current) {
      setTime(startTime);
    }
    const beginAt = Date.now();

    intervalRef.current = setInterval(() => {
      const nextTime = startTime + speedRef.current * (Date.now() - beginAt);

      if (nextTime >= maxTimeRef.current) {
        setTime(maxTimeRef.current);
        stopTimer();
        return;
      }

      setTime(nextTime);
    }, TICK_MS);

    setIsPlay(true);
  }, [clearTimerInterval, setTime, stopTimer]);

  useEffect(() => {
    if (isPlayRef.current) {
      startTimer();
    }
  }, [options.speed, startTimer]);

  useEffect(() => {
    return () => {
      clearTimerInterval();
    };
  }, [clearTimerInterval]);

  const updateTime = (nextTime: number) => {
    stopTimer();
    setTime(nextTime);
  };

  const seek = (next: number) => {
    const upper = Math.max(maxTime, 0);
    updateTime(Math.min(Math.max(next, 0), upper));
  };

  const canPlay = maxTime > 0;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        flexShrink: 0,
        boxSizing: 'border-box',
        px: { xs: 1.5, md: 2 },
        py: 0.5,
        minHeight: 48,
        minWidth: 0,
        bgcolor: '#fff',
        borderTop: '1px solid #f2f2f4',
        width: '100%'
      }}
    >
      <IconButton
        aria-label={isPlay ? 'Pause' : 'Play'}
        disabled={!canPlay}
        onClick={isPlay ? stopTimer : startTimer}
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          bgcolor: 'primary.main',
          color: '#fff',
          '&:hover': { bgcolor: 'primary.dark' },
          '&.Mui-disabled': { bgcolor: '#f3a0c0', color: '#fff' }
        }}
      >
        {isPlay ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
      </IconButton>
      <IconButton
        aria-label="Back 30 seconds"
        disabled={!canPlay}
        onClick={() => seek(time - SKIP_MS)}
        sx={{ color: '#2a2a2a', width: 32, height: 32 }}
      >
        <SkipPreviousRoundedIcon />
      </IconButton>
      <IconButton
        aria-label="Forward 30 seconds"
        disabled={!canPlay}
        onClick={() => seek(time + SKIP_MS)}
        sx={{ color: '#2a2a2a', width: 32, height: 32 }}
      >
        <SkipNextRoundedIcon />
      </IconButton>
      <SelectSpeed />
      <Box sx={{ flex: '1 1 auto', minWidth: 48, px: 1.25, opacity: canPlay ? 1 : 0.45 }}>
        <TimeInputSlider value={time} onChange={updateTime} compact disabled={!canPlay} />
      </Box>
      <Typography
        sx={{
          color: '#9aa0a6',
          fontWeight: 600,
          fontSize: 14,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0
        }}
      >
        {formatDisplayTime(time)}
      </Typography>
    </Stack>
  );
};

export default Timer;
